import { error } from '~/lib/log'

export const MEMBER_STALE_TIME = 30 * 60 * 1000 // member から返ってくる値は短時間では変わらないため、十分な時間を設定する

type MemberApiRequestType = {
  name: string
  nameKana: string
  phoneNumber: string
  mailAddress: string
  password: string
  authCode: string
  key: string
}

/**
 * 会員登録
 */
export type RegisterMemberRequest = MemberApiRequestType
export async function callRegisterMember(
  origin: string,
  data: RegisterMemberRequest
): Promise<void> {
  const options = createRequestOptions('POST', data)
  const result = await fetch(`${origin}/api/v1/member`, options)
  if (!result.ok) {
    throw new Error(`error`)
  }
}

/**
 * ゲスト信用判定
 */
export type TrustedGuestRequest = Pick<MemberApiRequestType, 'name' | 'phoneNumber' | 'mailAddress'>
export type TrustedGuestResponse = { trusted: boolean }
export async function callTrustedGuest(
  origin: string,
  data: TrustedGuestRequest
): Promise<TrustedGuestResponse> {
  const options = createRequestOptions('POST', data)
  const result = await fetch(`${origin}/api/v1/guest/trusted/restaurant`, options)
  if (result.status === 400) {
    // 400エラーの場合は、信頼されていないゲストとして扱う
    return { trusted: false }
  }
  if (!result.ok) {
    throw new Error(`error`)
  }
  return await result.json()
}

/**
 * 会員信用判定
 */
export type TrustedMemberResponse = { trusted: boolean }
export async function callTrustedMember(origin: string): Promise<TrustedMemberResponse> {
  const options = createRequestOptions('GET')
  const result = await fetch(`${origin}/api/v1/member/trusted/restaurant`, options)
  if (result.status === 400 || result.status === 403) {
    // 400または403エラーの場合は、信頼されていない会員として扱う
    return { trusted: false }
  }
  if (!result.ok) {
    throw new Error(`error`)
  }
  return await result.json()
}

/**
 * メールアドレス信用判定
 * ブラックリストの判定を行う
 */
const validateEmailMessages = ['ValidMailAddress', 'BlackMailAddress'] as const
export type ValidateEmailMessage = (typeof validateEmailMessages)[number]
export type ValidateEmailRequest = Pick<MemberApiRequestType, 'mailAddress'>
export type ValidateEmailResponse = { message: ValidateEmailMessage }
export async function callValidateEmail(
  origin: string,
  data: ValidateEmailRequest,
  userAgent?: string
): Promise<ValidateEmailResponse> {
  const options = createRequestOptions('POST', data, userAgent)
  const result = await fetch(`${origin}/api/v1/mailaddress/validate`, options)
  if (result.status === 400) {
    const response = await result.json()
    if (validateEmailMessages.includes(response?.name)) {
      return { message: response.name }
    } else {
      if (typeof window === 'undefined') {
        error(
          `[node.js fetch error] url=${origin}/api/v1/mailaddress/validate status=${result.status} headers=${JSON.stringify(options.headers)}`
        )
      }
      throw new Error(`Unknown response message`)
    }
  }
  if (!result.ok) {
    if (typeof window === 'undefined') {
      error(
        `[node.js fetch error] url=${origin}/api/v1/mailaddress/validate status=${result.status} headers=${JSON.stringify(options.headers)}`
      )
    }
    throw new Error(`error`)
  }
  return { message: 'ValidMailAddress' }
}

/**
 * 電話番号判定
 * ブラックリスト及び携帯電話の判定を行う
 */
const validatePhoneNumberMessages = [
  'ValidPhoneNumber',
  'NotMobilePhoneNumber',
  'BlackPhoneNumber',
  'InvalidPhoneNumber',
] as const
export type ValidatePhoneNumberMessage = (typeof validatePhoneNumberMessages)[number]
export type ValidatePhoneNumberRequest = Pick<MemberApiRequestType, 'phoneNumber'>
export type ValidatePhoneNumberResponse = { message: ValidatePhoneNumberMessage }
export async function callValidatePhoneNumber(
  origin: string,
  data: ValidatePhoneNumberRequest,
  // node.js から呼ぶときは MacaronFrontend を user-agent に指定しないといけない
  userAgent?: string
): Promise<ValidatePhoneNumberResponse> {
  const options = createRequestOptions('POST', data, userAgent)
  const result = await fetch(`${origin}/api/v1/phonenumber/validate`, options)
  if (result.status === 400) {
    const response = await result.json()
    if (validatePhoneNumberMessages.includes(response?.name)) {
      return { message: response.name }
    } else {
      throw new Error(`Unknown response message`)
    }
  }
  if (!result.ok) {
    if (typeof window === 'undefined') {
      error(
        `[node.js fetch error] url=${origin}/api/v1/phonenumber/validate status=${result.status} headers=${JSON.stringify(options.headers)}`
      )
    }
    throw new Error(`error`)
  }
  return { message: 'ValidPhoneNumber' }
}

/**
 * 既に会員登録されている電話番号か判定
 */
export type ExistsPhoneNumberRequest = Pick<MemberApiRequestType, 'phoneNumber'>
export type ExistsPhoneNumberResponse = { exists: boolean }
export async function callExistsPhoneNumber(
  origin: string,
  data: ExistsPhoneNumberRequest
): Promise<ExistsPhoneNumberResponse> {
  const options = createRequestOptions('POST', data)
  const result = await fetch(`${origin}/api/v1/member/phonenumber/exists?onlyid=true`, options)
  if (!result.ok) {
    throw new Error(`error`)
  }
  return await result.json()
}

/**
 * 既に会員登録されている電話番号か判定
 * onlyid=true が付与された場合は、電話番号認証している電話番号かどうかだけを見る
 * 具体的には TM_MEMBER の HOME_TEL は見ず、 TU_MEMBER_ID_PHONE_NUMBER のレコードがあるかどうかを見る
 */
export type ExistEmailRequest = Pick<MemberApiRequestType, 'mailAddress'>
export type ExistEmailResponse = { exists: boolean }
export async function callExistsEmail(
  origin: string,
  data: ExistEmailRequest
): Promise<ExistEmailResponse> {
  const options = createRequestOptions('POST', data)
  const result = await fetch(`${origin}/api/v1/member/mailaddress/exists`, options)
  if (!result.ok) {
    throw new Error(`error`)
  }
  return await result.json()
}

export async function callLogout(origin: string): Promise<void> {
  const options = createRequestOptions('DELETE')
  const result = await fetch(`${origin}/api/v1/member/logout`, options)
  if (!result.ok) {
    throw new Error(`error`)
  }
}

function createRequestOptions<T>(
  method: 'GET' | 'POST' | 'DELETE',
  data?: T,
  userAgent?: string
): RequestInit {
  const headers = (() => {
    const headers: HeadersInit = {}
    if (method !== 'GET') {
      // Member API は、GET以外のリクエストに 'X-Requested-With: XMLHttpRequest' ヘッダーを要求する
      // @see: https://member-apib.s3-ap-northeast-1.amazonaws.com/api.html#overview
      headers['X-Requested-With'] = 'XMLHttpRequest'
    }
    if (data !== undefined) {
      headers['Content-Type'] = 'application/json'
    }
    if (userAgent !== undefined) {
      headers['User-Agent'] = userAgent
    }
    return Object.keys(headers).length > 0 ? headers : undefined
  })()
  const body = data !== undefined ? JSON.stringify(data) : undefined
  return {
    method,
    headers,
    body,
    credentials: 'include',
  }
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  const data = { test: 'test' }
  it('createRequestOptions', () => {
    expect(createRequestOptions('GET')).toEqual({
      method: 'GET',
      credentials: 'include',
    })
    expect(createRequestOptions('POST', data)).toEqual({
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
      credentials: 'include',
    })
    expect(createRequestOptions('DELETE')).toEqual({
      method: 'DELETE',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'include',
    })
  })
}
